import { Box } from '@mui/material'
import { MaterialReactTable } from 'material-react-table'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'

import { Loader, muiRowDragHandleProps, TableToolbar } from '@/components'
import { useMrtKeyboardNavigation, useTable, useTableHandlers } from '@/hooks'
import { ViewPermission, ViewPermissionEnum } from '@/rtkApi/mddApiSliceGeneratedV2'

import { useSystemUsers } from '../../../../utils'
import { useViewV2TableBlurSave } from '../../hooks/useViewV2TableBlurSave'
import { useViewDataContext } from '../../ViewDataContext'
import { useViewPermissionsColumnsV2 } from './useViewPermissionsColumnsV2'

// Flat permission type for table editing
export interface ViewPermissionFlat {
	id?: number
	order?: number,
	permissions?: ViewPermissionEnum[],
	userCode?: string
	userComment?: string,
	userDescription?: string
	userId?: number,
	userName?: string,
	uuid?: string
}

// Convert V2 ViewPermission to flat format for table
const toFlat = (permission: ViewPermission): ViewPermissionFlat => ({
	id: permission.id,
	uuid: permission.user?.code ?? uuidv4(),
	userId: permission.user?.id,
	userCode: permission.user?.code,
	userName: permission.user?.name,
	userDescription: permission.user?.description,
	userComment: permission.user?.comment,
	permissions: permission.permissions,
	order: permission.order,
})

// Convert flat format back to V2 ViewPermission
const fromFlat = (flat: ViewPermissionFlat): ViewPermission => ({
	id: flat.id,
	user: flat.userId
		? {
				id: flat.userId,
				code: flat.userCode,
				name: flat.userName,
				description: flat.userDescription,
				comment: flat.userComment,
		  }
		: undefined,
	permissions: flat.permissions,
	order: flat.order,
})

const createEmptyPermission = (): ViewPermissionFlat => ({
	id: undefined,
	uuid: uuidv4(),
	userId: undefined,
	permissions: ['SELECT'],
	order: 0,
})

export const PermissionsV2 = () => {
	const {
		permissions,
		editMode,
		systemNodeId,
		onPermissionsChange,
		onSave,
		dirty,
		isSaving,
		isLoading: isPermissionsLoading,
	} = useViewDataContext()
	const containerRef = useRef<HTMLDivElement>(null)

	const { data: systemUsers } = useSystemUsers(systemNodeId)

	// Convert V2 permissions to flat format
	const flatPermissions = useMemo(
		() => (permissions ?? []).map(toFlat),
		[permissions],
	)

	// Handle data changes - convert back to V2 format
	const handleDataChange = useCallback(
		(data: ViewPermissionFlat[]) => {
			const v2Permissions = data.map(fromFlat)
			onPermissionsChange(v2Permissions)
		},
		[onPermissionsChange],
	)

	const {
		isUserInteracting,
		setSelectedRow,
		setTableData,
		tableData,
		updateTableData,
		deleteRow,
		updateCellValue: _defaultUpdateCellValue,
	} = useTableHandlers<ViewPermissionFlat>({
		initialData: flatPermissions,
		// V2 callback mode
		onDataChange: handleDataChange,
	})

	// Custom update cell value that enriches user data
	const updateCellValue = useCallback(
		(rowIndex: number, columnId: keyof ViewPermissionFlat, value: unknown) => {
			if (!isUserInteracting.current || value === undefined) return

			// Enrich user data when userId changes
			if (columnId === 'userId' && value) {
				const user = systemUsers?.find((u) => u.id === value)
				updateTableData((draft) => {
					const row = draft[rowIndex]
					if (!row) return
					row.userId = value as number
					row.userCode = user?.code
					row.userComment = user?.comment
					row.userDescription = user?.description
					row.userName = user?.name
				})
			} else {
				updateTableData((draft) => {
					const row = draft[rowIndex]
					if (!row) return
					// Dynamic column assignment for ViewPermissionFlat (userId, userComment, permissionType)
					;(row as any)[columnId] = value
				})
			}
		},
		[systemUsers, updateTableData, isUserInteracting],
	)

	const columns = useViewPermissionsColumnsV2({
		systemUsers,
		updateCellValue,
	})

	// Handler to insert a new row at a specific index
	const handleInsertRow = useCallback(
		(rowIndex: number) => {
			const newRow: ViewPermissionFlat = {
				...createEmptyPermission(),
				id: Date.now(),
			}

			// updateTableData uses Immer, so mutate draft directly
			// rowIndex of -1 means insert at beginning (empty table case)
			const insertIndex = Math.max(0, rowIndex + 1)
			updateTableData((draft) => {
				draft.splice(insertIndex, 0, newRow)
			})
		},
		[updateTableData],
	)

	const table = useTable<ViewPermissionFlat>({
		columns,
		data: tableData,
		deleteRow,
		editMode,
		onInsertRow: handleInsertRow,
		tableId: 'view-v2-permissions',
		options: {
			enableEditing: true,
			editDisplayMode: 'table',
			renderBottomToolbar: () => (
				<TableToolbar<ViewPermissionFlat>
					updateTableData={updateTableData}
					newRowPayload={createEmptyPermission}
					tableData={tableData}
					setSelectedRow={setSelectedRow}
				/>
			),
			muiRowDragHandleProps: ({ table }) =>
				muiRowDragHandleProps({ updateTableData, table, editMode }),
		},
	})

	// Keyboard navigation
	useMrtKeyboardNavigation({
		table,
		editMode,
		onInsertRow: handleInsertRow,
		deleteRow,
		containerRef,
		onSave,
	})

	useViewV2TableBlurSave({
		containerRef,
		editMode,
		dirty,
		isSaving,
		onSave,
	})

	// Set isUserInteracting on mount to ensure selects work properly
	// (react-select renders menu in portal outside our container)
	useEffect(() => {
		isUserInteracting.current = true
	}, [isUserInteracting])

	// Sync from parent when data is loaded or structure changes (add/remove row).
	// In edit mode, when length is unchanged we do not overwrite — reorder is done in table
	// and onPermissionsChange updates parent; overwriting with stale flatPermissions would undo reorder.
	useEffect(() => {
		if (!flatPermissions || flatPermissions.length === 0) {
			setTableData([createEmptyPermission()])
			return
		}
		if (!editMode) {
			setTableData(flatPermissions)
			return
		}
		setTableData((current) =>
			current.length !== flatPermissions.length ? flatPermissions : current,
		)
	}, [flatPermissions, editMode, setTableData])

	if (isPermissionsLoading) {
		return <Loader loaded={false} />
	}

	return (
		<Box
			ref={containerRef}
			sx={{ height: '100%', width: '100%', overflow: 'auto', p: 2 }}
			tabIndex={0}
		>
			<MaterialReactTable table={table} />
		</Box>
	)
}
